<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'

import PlayoutScheduleControls from './PlayoutScheduleControls.vue'
import PlayoutScheduleEmpty from './PlayoutScheduleEmpty.vue'
import PlayoutScheduleTable from './PlayoutScheduleTable.vue'
import PlayoutLimitWarning from './PlayoutLimitWarning.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'

const props = defineProps({
	mediaPulse: {
		type: Object,
		default: null,
	},
	configLoaded: {
		type: Boolean,
		default: true,
	},
	limitsLoading: {
		type: Boolean,
		default: false,
	},
	scheduleBlocked: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['enable-stream', 'browse-files', 'add-source', 'add-break', 'view-limit-detail'])

const store = useStore()
const { notify } = useNotification()

const playout = computed(() => store.getters['Streams/tvPlayout'])
const playlist = computed(() => store.getters['Playout/playlist'] || [])
const hasItems = computed(() => playlist.value.length > 0)

const totalRuntimeMs = computed(() =>
	playlist.value.reduce((sum, item) => {
		const span = (item.endTime || 0) - (item.startTime || 0)
		return sum + (span > 0 ? span : 0)
	}, 0),
)
const totalRuntimeLabel = computed(() => {
	const totalMin = Math.round(totalRuntimeMs.value / 60000)
	if (totalMin < 1) return '<1m'
	const days = Math.floor(totalMin / 1440)
	const hours = Math.floor((totalMin % 1440) / 60)
	const mins = totalMin % 60
	if (days > 0) return `${days}d ${hours}h`
	if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
	return `${mins}m`
})

const removeAll = () => {
	showModal('playout-v2-remove-all' + playout.value._id)
}

const onConfirmRemoveAll = async () => {
	const items = [...playlist.value]
	for (const item of items) {
		await store.dispatch('Playout/deleteItem', item)
	}
	notify({ group: 'success', text: 'All items removed from schedule' })
}

const addBreak = () => {
	store.dispatch('Playout/addItem', null)
	emit('add-break')
}
</script>

<template>
	<section class="flex flex-col gap-4">
		<template v-if="configLoaded">
			<PlayoutScheduleControls
				:media-pulse="mediaPulse"
				:limits-exceeded="scheduleBlocked"
				:limits-loading="limitsLoading"
				@enable-stream="emit('enable-stream', $event)"
			/>

			<PlayoutLimitWarning v-if="scheduleBlocked" @view-detail="emit('view-limit-detail')" />

			<header class="flex flex-wrap items-center gap-2">
				<h3 class="min-w-0 mr-auto text-base font-medium text-white m-0">
					Schedule list
					<span v-if="hasItems" class="hidden sm:inline text-tiny font-normal text-surface-7 ml-1.5">
						{{ playlist.length }} {{ playlist.length === 1 ? 'item' : 'items' }} · {{ totalRuntimeLabel }}
					</span>
				</h3>
				<FormButton
					v-if="hasItems"
					type="link"
					customClasses="order-last basis-full sm:order-none sm:basis-auto text-error hover:text-error-light text-sm inline-flex items-center"
					@click="removeAll"
				>
					Remove all
				</FormButton>
				<FormButton type="secondary" class="shrink-0" @click="addBreak">Add break</FormButton>
				<FormButton type="main" class="shrink-0" @click="emit('add-source')">
					<icon-base class="mr-2">
						<icon-plus />
					</icon-base>
					Add source
				</FormButton>
			</header>

			<PlayoutScheduleEmpty
				v-if="!hasItems"
				@browse-files="emit('browse-files')"
				@add-source="emit('add-source')"
			/>
			<PlayoutScheduleTable v-else :media-pulse="mediaPulse" />
		</template>
		<template v-else>
			<div class="bg-surface-2 rounded-lg p-3 sm:p-4 flex flex-col gap-3 animate-pulse">
				<div class="flex flex-col sm:flex-row sm:flex-wrap gap-3">
					<div class="h-8 w-full sm:w-40 rounded-md bg-surface-3"></div>
					<div class="h-8 w-full sm:w-48 rounded-md bg-surface-3"></div>
					<div class="h-8 w-full sm:w-36 rounded-md bg-surface-3"></div>
					<div class="h-8 w-28 rounded-md bg-surface-3 sm:ml-auto"></div>
				</div>
			</div>
			<div class="flex flex-wrap items-center gap-2">
				<div class="h-5 w-32 rounded bg-surface-3 animate-pulse"></div>
				<div class="h-8 w-24 rounded-md bg-surface-3 animate-pulse ml-auto"></div>
				<div class="h-8 w-28 rounded-md bg-surface-3 animate-pulse"></div>
			</div>
			<div class="flex flex-col gap-2">
				<div v-for="n in 3" :key="n" class="h-14 rounded-lg bg-surface-3 animate-pulse"></div>
			</div>
		</template>

		<ConfirmModal
			:modalId="'playout-v2-remove-all' + playout?._id"
			message="Remove all items?"
			body="This will remove all content from the schedule list. This action cannot be undone."
			cancel-text="Cancel"
			ok-text="Remove all"
			modal-type="danger"
			@modal-confirm="onConfirmRemoveAll"
		/>
	</section>
</template>

<style scoped></style>
